import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Settings, Trash2, ChevronDown, Edit, Save, X } from 'lucide-react';
import { MappingEndpointsSection } from './MappingEndpointsSection';
import { cn } from '@/lib/utils';

interface Bot {
  id: string;
  name: string;
  signal_token: string;
  type: string;
}

interface BotMapping {
  id: string;
  parent_bot_id: string;
  child_bot_id: string;
  is_active: boolean;
  notes: string | null;
  instrument: string | null;
  tp_usd: number | null;
  sl_usd: number | null;
  quantity: number | null;
  volume_multiplier: number;
  created_at: string;
  updated_at: string;
  parent_bot: Bot | null;
  child_bot: Bot | null;
}

interface ChildBotItemProps {
  mapping: BotMapping;
  onToggleActive: (mapping: BotMapping) => void;
  onUpdate: (data: {
    id: string;
    volume_multiplier?: number;
  }) => void;
  onDelete: (mapping: BotMapping) => void;
  isUpdating: boolean;
  isDeleting: boolean;
}

export const ChildBotItem: React.FC<ChildBotItemProps> = ({
  mapping,
  onToggleActive,
  onUpdate,
  onDelete,
  isUpdating,
  isDeleting,
}) => {
  const [showEndpoints, setShowEndpoints] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editVolumeMultiplier, setEditVolumeMultiplier] = useState(mapping.volume_multiplier?.toString() || '1');
  const childBot = mapping.child_bot;
  const parentBot = mapping.parent_bot;

  const handleSave = () => {
    onUpdate({
      id: mapping.id,
      volume_multiplier: editVolumeMultiplier ? parseFloat(editVolumeMultiplier) : 1.0,
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditVolumeMultiplier(mapping.volume_multiplier?.toString() || '1');
    setIsEditing(false);
  };

  if (!childBot || !parentBot) {
    return (
      <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-destructive text-sm">Invalid Mapping</p>
            <p className="text-xs text-muted-foreground">Bot đã bị xóa</p>
          </div>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => onDelete(mapping)}
            disabled={isDeleting}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'border rounded-lg transition-all',
        mapping.is_active ? 'bg-card border-border' : 'bg-muted/30 border-muted'
      )}
    >
      <div className="flex items-center justify-between gap-3 p-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-sm truncate">{childBot.name}</span>
              <Badge variant="secondary" className="text-xs">
                {childBot.type}
              </Badge>
              {mapping.is_active ? (
                <Badge variant="default" className="text-xs">Active</Badge>
              ) : (
                <Badge variant="outline" className="text-xs">Inactive</Badge>
              )}
            </div>
            {mapping.notes && (
              <p className="text-xs text-muted-foreground truncate">{mapping.notes}</p>
            )}
            
            {/* Edit Mode */}
            {isEditing ? (
              <div className="space-y-3 p-3 bg-muted/30 rounded-lg mt-2">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor={`volume-${mapping.id}`} className="text-sm font-medium">
                      Volume Multiplier
                    </Label>
                    <span className="text-xs text-muted-foreground">
                      Realtime Signal Config
                    </span>
                  </div>
                  <Input
                    id={`volume-${mapping.id}`}
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="100"
                    value={editVolumeMultiplier}
                    onChange={(e) => setEditVolumeMultiplier(e.target.value)}
                    className="h-9"
                  />
                  <p className="text-xs text-muted-foreground">
                    📡 Signal amount sẽ được nhân với hệ số này khi forward đến parent bot
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={handleSave} disabled={isUpdating}>
                    <Save className="h-3 w-3 mr-1" />
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancel}>
                    <X className="h-3 w-3 mr-1" />
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {/* Trading Config Display - Read Only */}
                <div className="flex items-center gap-2 text-xs flex-wrap">
                  {mapping.instrument && mapping.tp_usd && mapping.sl_usd && mapping.quantity && (
                    <>
                      <span className="text-muted-foreground">📊 Config synced from Historical Analysis:</span>
                      <span className="font-mono bg-muted px-1.5 py-0.5 rounded">{mapping.instrument}</span>
                      <span className="bg-green-500/10 text-green-700 dark:text-green-400 px-1.5 py-0.5 rounded">
                        TP: ${mapping.tp_usd}
                      </span>
                      <span className="bg-red-500/10 text-red-700 dark:text-red-400 px-1.5 py-0.5 rounded">
                        SL: ${mapping.sl_usd}
                      </span>
                      <span className="bg-blue-500/10 text-blue-700 dark:text-blue-400 px-1.5 py-0.5 rounded">
                        Qty: {mapping.quantity}
                      </span>
                    </>
                  )}
                  {mapping.volume_multiplier && mapping.volume_multiplier !== 1.0 && (
                    <span className="bg-purple-500/10 text-purple-700 dark:text-purple-400 px-1.5 py-0.5 rounded font-medium">
                      Vol: {mapping.volume_multiplier}x
                    </span>
                  )}
                  {!mapping.instrument && !mapping.tp_usd && !mapping.sl_usd && !mapping.quantity && (
                    <div className="flex items-center gap-1.5">
                      <Badge variant="outline" className="text-blue-600 border-blue-300 bg-blue-50 dark:bg-blue-950/20">
                        ℹ️ Not Configured
                      </Badge>
                      <span className="text-muted-foreground">Configure in Historical Analysis tab</span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Switch
            checked={mapping.is_active}
            onCheckedChange={() => onToggleActive(mapping)}
            disabled={isUpdating || isEditing}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsEditing(!isEditing)}
            disabled={isUpdating}
            className="h-8 w-8 p-0"
          >
            <Edit className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowEndpoints(!showEndpoints)}
            className="h-8 w-8 p-0"
          >
            <Settings className={cn(
              "h-3.5 w-3.5 transition-transform",
              showEndpoints && "rotate-90"
            )} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(mapping)}
            disabled={isDeleting || isEditing}
            className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {showEndpoints && (
        <div className="border-t p-3 bg-muted/20">
          <MappingEndpointsSection
            mappingId={mapping.id}
            parentBotId={mapping.parent_bot_id}
            parentBotName={parentBot.name}
            childBotName={childBot.name}
          />
        </div>
      )}
    </div>
  );
};
